import { useEffect, useState } from 'react'
import * as api from '../lib/api'
import { useI18n } from '../i18n/I18nProvider'

export const CONFIGURED_APPRAISAL_LAYOUT = 'objectives_manager_rating'
export const DEFAULT_OBJECTIVE_ROW = { priority: '', objective: '' }
const EMPTY_FORM_STRUCTURE = []

export const getAppraisalFormConfig = formConfig => formConfig?.appraisal_form || {}

export const usesConfiguredAppraisalLayout = formConfig => (
  getAppraisalFormConfig(formConfig).layout === CONFIGURED_APPRAISAL_LAYOUT
)

export const isConfiguredFormSectionEnabled = (formConfig, audience, section, fallback = true) => {
  const sections = getAppraisalFormConfig(formConfig).sections?.[audience]
  return Array.isArray(sections) ? sections.includes(section) : fallback
}

export const getConfiguredOverallRatingOptions = formConfig => {
  const options = getAppraisalFormConfig(formConfig).overall_rating_options
  return Array.isArray(options) ? options : []
}

export const getConfiguredFactorRatingOptions = formConfig => {
  const options = getAppraisalFormConfig(formConfig).factor_rating_options
  return Array.isArray(options) ? options : []
}

export const getConfiguredOverallRatingField = formConfig => (
  getAppraisalFormConfig(formConfig).overall_rating_field || 'overall_rating'
)

export const getConfiguredOverallRatingValue = (formData, formConfig) => (
  formData?.[getConfiguredOverallRatingField(formConfig)]
)

export const getConfiguredObjectiveRow = formConfig => ({
  ...DEFAULT_OBJECTIVE_ROW,
  ...(getAppraisalFormConfig(formConfig).objective_row || {})
})

export const getConfiguredTitle = (formConfig, key, fallback) => (
  getAppraisalFormConfig(formConfig).titles?.[key] || fallback
)

// An explicitly empty displayCode shows the label alone (e.g. "New In Position (new)").
export const ratingOptionText = option => (
  option.displayCode === '' ? option.label : `${option.displayCode || option.value} - ${option.label}`
)

export const configuredRatingLabel = (value, formConfig) => {
  const option = getConfiguredOverallRatingOptions(formConfig).find(item => item.value === value)
  return option ? ratingOptionText(option) : value || 'Not rated'
}

export const objectivePriorityTotal = rows => Number(rows.reduce(
  (sum, row) => sum + (parseFloat(row.priority) || 0),
  0
).toFixed(2))

export const hasObjectiveEntry = row => Boolean(
  (row?.objective || '').trim() ||
  (row?.achievement_examples || '').trim() ||
  `${row?.priority ?? ''}`.trim()
)

export const hasObjectiveText = row => Boolean((row?.objective || '').trim())

export function useFormStructure (businessUnitCode, fallbackStructure = EMPTY_FORM_STRUCTURE, { translationReview = false } = {}) {
  const { locale } = useI18n()
  const [formStructure, setFormStructure] = useState([])
  const [formConfig, setFormConfig] = useState({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!businessUnitCode) {
      setFormStructure([])
      setFormConfig({})
      setError(null)
      setLoading(false)
      return
    }

    let active = true
    setLoading(true)
    setError(null)
    api.getFormStructure(businessUnitCode, locale, { translationReview })
      .then(data => {
        if (!active) return
        setFormStructure(Array.isArray(data.categories) ? data.categories : [])
        setFormConfig(data.form_config && typeof data.form_config === 'object' ? data.form_config : {})
      })
      .catch(error => {
        if (!active) return
        setFormStructure([])
        setFormConfig({})
        setError(error)
      })
      .finally(() => {
        if (active) setLoading(false)
      })

    return () => {
      active = false
    }
  }, [businessUnitCode, fallbackStructure, locale, translationReview])

  return { formStructure, formConfig, loading, error }
}
